<script lang="ts" setup>
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLayout } from 'vuetify'
import { useCookieConsent } from '@/composables/useCookieConsent'
import { useGoogleAds } from '@/composables/useGoogleAds'

const { t } = useI18n()
const { showBanner, hasAcceptedMarketing, acceptAll, declineMarketing } = useCookieConsent()
const { loadGtag } = useGoogleAds()

// The banner is fixed to the viewport, outside <v-main>, so it does not get the
// layout padding Vuetify applies there. Under 700px the landing pages render a
// v-bottom-navigation, which is a layout item at a higher z-index, and it covered
// the accept button entirely. mainRect.bottom is the space every bottom layout
// item occupies (0 when there is none, e.g. desktop and dashboard).
const { mainRect } = useLayout()
const bannerStyle = computed(() => ({ bottom: `${mainRect.value.bottom}px` }))

const handleAcceptAll = () => {
  acceptAll()
  loadGtag()
}

const handleDecline = () => {
  declineMarketing()
}

onMounted(() => {
  if (hasAcceptedMarketing.value) {
    loadGtag()
  }
})
</script>

<template>
  <Transition name="consent-banner">
    <div
      v-if="showBanner"
      class="consent-banner"
      :style="bannerStyle"
      role="dialog"
      :aria-label="t('cookieConsent.ariaLabel')"
    >
      <div class="consent-banner__inner">
        <div class="consent-banner__icon">
          <v-icon icon="mdi-cookie" size="22" aria-hidden="true" />
        </div>
        <div class="consent-banner__content">
          <p class="consent-banner__text">
            {{ t('cookieConsent.text') }}
            <router-link :to="{ name: 'privacy' }" class="consent-banner__link">
              {{ t('cookieConsent.learnMore') }}
            </router-link>
          </p>
        </div>
        <div class="consent-banner__actions">
          <button
            type="button"
            class="consent-banner__btn consent-banner__btn--decline"
            @click="handleDecline"
          >
            {{ t('cookieConsent.declineBtn') }}
          </button>
          <button
            type="button"
            class="consent-banner__btn consent-banner__btn--accept"
            @click="handleAcceptAll"
          >
            {{ t('cookieConsent.acceptBtn') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  padding: 16px 24px;
  background: rgba(15, 23, 48, 0.98);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(0, 176, 255, 0.2);
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.3);

  &__inner {
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: 1200px;
    margin: 0 auto;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(0, 176, 255, 0.12);
    border-radius: 10px;
    color: #00b0ff;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__text {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    line-height: 1.5;
  }

  &__link {
    color: #00b0ff;
    text-decoration: none;
    margin-left: 4px;

    &:hover {
      text-decoration: underline;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  &__btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    border: none;
    border-radius: 8px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &--accept {
      background: #00b0ff;
      color: #fff;

      &:hover {
        background: #33c0ff;
      }
    }

    &--decline {
      background: rgba(120, 163, 255, 0.1);
      color: rgba(200, 218, 255, 0.85);
      border: 1px solid rgba(120, 163, 255, 0.2);

      &:hover {
        background: rgba(120, 163, 255, 0.18);
        border-color: rgba(120, 163, 255, 0.3);
      }
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .consent-banner {
    background: rgba(255, 255, 255, 0.98);
    border-top-color: rgba(28, 110, 227, 0.2);
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.1);

    &__icon {
      background: rgba(28, 110, 227, 0.1);
      color: #1c6ee3;
    }

    &__text {
      color: rgba(15, 23, 42, 0.85);
    }

    &__link {
      color: #1c6ee3;
    }

    &__btn {
      &--accept {
        background: #1c6ee3;

        &:hover {
          background: #3d85eb;
        }
      }

      &--decline {
        background: rgba(28, 110, 227, 0.08);
        color: rgba(15, 23, 42, 0.75);
        border-color: rgba(28, 110, 227, 0.15);

        &:hover {
          background: rgba(28, 110, 227, 0.12);
          border-color: rgba(28, 110, 227, 0.25);
        }
      }
    }
  }
}

/* Slide-up animation */
.consent-banner-enter-active {
  animation: banner-in 0.3s ease-out;
}

.consent-banner-leave-active {
  animation: banner-out 0.2s ease-in;
}

@keyframes banner-in {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes banner-out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
}

/* Mobile adjustments */
@media (max-width: 700px) {
  .consent-banner {
    padding: 16px;

    &__inner {
      flex-direction: column;
      gap: 12px;
      text-align: center;
    }

    &__icon {
      display: none;
    }

    &__actions {
      width: 100%;
      flex-direction: column;
    }

    &__btn {
      width: 100%;
      justify-content: center;
    }
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .consent-banner-enter-active,
  .consent-banner-leave-active {
    animation: none;
  }

  .consent-banner__btn {
    transition: none;
  }
}
</style>
